import React, { useCallback, useState } from 'react';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth, useSignIn } from '@clerk/clerk-react';
import { Alert, Box, Button, CircularProgress, Link, Paper, Typography } from '@mui/material';

import FullPageLoader from './FullPageLoader';
import Section from './Section';
import { getClerkPublishableKey } from './clerkPublishableKey';
import GoogleGLogo from './GoogleGLogo';
import useClerkLoadTimeout from './useClerkLoadTimeout';

export const LOGIN_HOME_SECTION_ID = 'subscription';

const SSO_HEIGHT = 52;
const SSO_RADIUS = 26;

const loginHomeSectionSx = {
  bgcolor: 'transparent',
  color: 'text.primary',
  scrollMarginTop: '16px',
};

/** Совпадает с тёмной темой в overlay-carousel `AccountSignInModal` (Google guidelines). */
const GOOGLE_DARK = {
  fill: '#131314',
  stroke: '#8E918F',
  text: '#E3E3E3',
};

/** Apple: белая кнопка на тёмном фоне — как `APPLE_AS_WHITE_ON_DARK` в overlay-carousel. */
const APPLE_WHITE_ON_DARK = {
  bg: '#FFFFFF',
  fg: '#000000',
  border: 'rgba(60, 60, 67, 0.36)',
};

function AppleMark({ color, size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false">
      <path
        fill={color}
        d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
      />
    </svg>
  );
}

function OAuthButtons({ busy, loading, onGoogle, onApple, t }) {
  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 296,
        flex: 'none',
        mt: { xs: 4, sm: 0 },
        ml: { xs: 0, sm: 5 },
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <Button
        fullWidth
        onClick={onGoogle}
        disabled={busy}
        sx={{
          height: SSO_HEIGHT,
          borderRadius: `${SSO_RADIUS}px`,
          bgcolor: GOOGLE_DARK.fill,
          color: GOOGLE_DARK.text,
          border: `1px solid ${GOOGLE_DARK.stroke}`,
          textTransform: 'none',
          fontSize: '1rem',
          fontWeight: 600,
          justifyContent: 'center',
          gap: 1.5,
          '&:hover': { bgcolor: GOOGLE_DARK.fill, opacity: 0.92 },
        }}
      >
        {loading === 'google' ? (
          <CircularProgress size={24} sx={{ color: GOOGLE_DARK.text }} />
        ) : (
          <GoogleGLogo size={22} />
        )}
        {t('login.google')}
      </Button>

      <Button
        fullWidth
        onClick={onApple}
        disabled={busy}
        sx={{
          height: SSO_HEIGHT,
          borderRadius: `${SSO_RADIUS}px`,
          bgcolor: APPLE_WHITE_ON_DARK.bg,
          color: APPLE_WHITE_ON_DARK.fg,
          border: `1px solid ${APPLE_WHITE_ON_DARK.border}`,
          textTransform: 'none',
          fontSize: '1rem',
          fontWeight: 600,
          justifyContent: 'center',
          gap: 1.5,
          '&:hover': { bgcolor: APPLE_WHITE_ON_DARK.bg, opacity: 0.92 },
        }}
      >
        {loading === 'apple' ? (
          <CircularProgress size={24} sx={{ color: APPLE_WHITE_ON_DARK.fg }} />
        ) : (
          <AppleMark color={APPLE_WHITE_ON_DARK.fg} />
        )}
        {t('login.apple')}
      </Button>

      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5, mt: 0.5, textAlign: 'center' }}>
        <Trans
          i18nKey="login.consent"
          components={{
            privacy: (
              <Link
                href="/web-privacy"
                target="_blank"
                rel="noopener noreferrer"
                color="inherit"
                underline="always"
                align="center"
              />
            ),
          }}
        />
      </Typography>
    </Box>
  );
}

/** Блок входа под главным экраном на «/» — без редиректа и без полноэкранного лоадера. */
export function LoginHomeSection() {
  const publishableKey = getClerkPublishableKey();
  const { t } = useTranslation();

  if (!publishableKey) {
    return (
      <Section id={LOGIN_HOME_SECTION_ID} sx={loginHomeSectionSx}>
        <Box sx={{ width: '100%', maxWidth: 560, py: 2 }}>
          <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
            {t('nav.webSubscription')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('login.missingKey')}
          </Typography>
        </Box>
        <Box height={24} />
      </Section>
    );
  }

  return <LoginHomeSectionWithClerk />;
}

function LoginHomeSectionWithClerk() {
  const { t } = useTranslation();
  const { isLoaded: authLoaded, isSignedIn } = useAuth();
  const { isLoaded: signInLoaded, signIn } = useSignIn();
  const [loading, setLoading] = useState(null);
  const clerkReady = authLoaded && signInLoaded;
  const clerkTimedOut = useClerkLoadTimeout(clerkReady);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const redirectUrl = `${origin}/sso-callback`;
  const redirectUrlComplete = `${origin}/account`;

  const onGoogle = useCallback(async () => {
    if (!signIn || loading) return;
    setLoading('google');
    try {
      await signIn.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl,
        redirectUrlComplete,
      });
    } catch (e) {
      console.warn('[Clerk] Google sign-in:', e?.message || e);
    } finally {
      setLoading(null);
    }
  }, [signIn, loading, redirectUrl, redirectUrlComplete]);

  const onApple = useCallback(async () => {
    if (!signIn || loading) return;
    setLoading('apple');
    try {
      await signIn.authenticateWithRedirect({
        strategy: 'oauth_apple',
        redirectUrl,
        redirectUrlComplete,
      });
    } catch (e) {
      console.warn('[Clerk] Apple sign-in:', e?.message || e);
    } finally {
      setLoading(null);
    }
  }, [signIn, loading, redirectUrl, redirectUrlComplete]);

  if (!clerkReady && !clerkTimedOut) {
    return (
      <Section id={LOGIN_HOME_SECTION_ID} sx={loginHomeSectionSx}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Box>
        <Box height={24} />
      </Section>
    );
  }

  const busy = loading !== null;

  return (
    <Section id={LOGIN_HOME_SECTION_ID} sx={loginHomeSectionSx}>
      <Box
        display="flex"
        alignItems="center"
        sx={{ flexDirection: { xs: 'column', sm: 'row' }, width: '100%' }}
      >
        <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
          <Box mb={3}>
            <Typography variant="h4">{t('nav.webSubscription')}</Typography>
          </Box>
          <Typography variant="body1" sx={{ mb: 2, maxWidth: 480, lineHeight: 1.6 }}>
            {t('login.subtitle1')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, maxWidth: 480, lineHeight: 1.6 }}>
            {t('login.subtitle2')}
          </Typography>
          {clerkTimedOut && (
            <Alert severity="error" sx={{ mt: 2, maxWidth: 480 }}>
              {t('login.serviceUnavailable')}
            </Alert>
          )}
        </Box>

        {isSignedIn ? (
          <Box
            sx={{
              width: '100%',
              maxWidth: 296,
              flex: 'none',
              mt: { xs: 4, sm: 0 },
              ml: { xs: 0, sm: 5 },
            }}
          >
            <Button
              fullWidth
              component={RouterLink}
              to="/account"
              variant="outlined"
              color="inherit"
              sx={{
                height: SSO_HEIGHT,
                borderRadius: `${SSO_RADIUS}px`,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
              }}
            >
              {t('account.title')}
            </Button>
          </Box>
        ) : (
          <OAuthButtons busy={busy} loading={loading} onGoogle={onGoogle} onApple={onApple} t={t} />
        )}
      </Box>
      <Box height={24} />
    </Section>
  );
}

export default function Login() {
  const publishableKey = getClerkPublishableKey();
  const { t } = useTranslation();

  if (!publishableKey) {
    return (
      <Section sx={{ bgcolor: 'background.default', color: 'text.primary' }}>
        <Box sx={{ width: '100%', maxWidth: 560, py: 4 }}>
          <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
            {t('login.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('login.missingKey')}
          </Typography>
        </Box>
      </Section>
    );
  }

  return <LoginWithClerk />;
}

function LoginWithClerk() {
  const { t } = useTranslation();
  const { isLoaded: authLoaded, isSignedIn } = useAuth();
  const { isLoaded: signInLoaded, signIn } = useSignIn();
  const [loading, setLoading] = useState(null);
  const clerkReady = authLoaded && signInLoaded;
  const clerkTimedOut = useClerkLoadTimeout(clerkReady);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const redirectUrl = `${origin}/sso-callback`;
  const redirectUrlComplete = `${origin}/account`;

  const onGoogle = useCallback(async () => {
    if (!signIn || loading) return;
    setLoading('google');
    try {
      await signIn.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl,
        redirectUrlComplete,
      });
    } catch (e) {
      console.warn('[Clerk] Google sign-in:', e?.message || e);
    } finally {
      setLoading(null);
    }
  }, [signIn, loading, redirectUrl, redirectUrlComplete]);

  const onApple = useCallback(async () => {
    if (!signIn || loading) return;
    setLoading('apple');
    try {
      await signIn.authenticateWithRedirect({
        strategy: 'oauth_apple',
        redirectUrl,
        redirectUrlComplete,
      });
    } catch (e) {
      console.warn('[Clerk] Apple sign-in:', e?.message || e);
    } finally {
      setLoading(null);
    }
  }, [signIn, loading, redirectUrl, redirectUrlComplete]);

  if (!clerkReady && !clerkTimedOut) {
    return <FullPageLoader />;
  }

  if (isSignedIn) {
    return <Navigate to="/account" replace />;
  }

  const busy = loading !== null;

  return (
    <Section sx={{ bgcolor: 'background.default', color: 'text.primary' }}>
      <Box
        display="flex"
        alignItems="center"
        sx={{ flexDirection: { xs: 'column', sm: 'row' }, width: '100%' }}
      >
        <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
          <Box display="flex" alignItems="center" mb={3}>
            <Paper
              sx={{
                width: 64,
                height: 64,
                borderRadius: 2,
                mr: 2,
                overflow: 'hidden',
                bgcolor: '#000000',
              }}
              elevation={5}
            >
              <img src="/lett-logo.webp" alt="" width={64} height={64} />
            </Paper>
            <Typography variant="h2" component="span">
              Lett
            </Typography>
          </Box>
          <Box mb={3}>
            <Typography variant="h4">{t('login.title')}</Typography>
          </Box>
          <Typography variant="body1" sx={{ mb: 2, maxWidth: 480, lineHeight: 1.6 }}>
            {t('login.subtitle1')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, maxWidth: 480, lineHeight: 1.6 }}>
            {t('login.subtitle2')}
          </Typography>
          {clerkTimedOut && (
            <Alert severity="error" sx={{ mt: 2, maxWidth: 480 }}>
              {t('login.serviceUnavailable')}
            </Alert>
          )}
        </Box>

        <OAuthButtons busy={busy} loading={loading} onGoogle={onGoogle} onApple={onApple} t={t} />
      </Box>
      <Box height={24} />
    </Section>
  );
}
